import { useState, useEffect, useCallback, useRef } from 'react'
import { getOperations, deleteOperation, addOperation, getCategories, getParametres, getComptes } from '../services/api'
import { Amount, formatMoneySimple } from '../utils/formatMoney'
import { categoryIcon } from '../utils/categoryIcon'
import OperationModal from '../components/OperationModal'
import Icon from '../components/Icon'
import { useToast } from '../components/ToastProvider'
import { useConfirm } from '../components/ConfirmProvider'
import { useFreshData } from '../hooks/useFreshData'
import { isAbortError } from '../utils/abort'
import { formatLocalISO, formatOperationDayLabel, formatOperationLongDate } from '../utils/dates'

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

const TYPE_FILTERS = [
  { key: 'tout', label: 'Tout' },
  { key: 'entrée', label: 'Entrées' },
  { key: 'dépense', label: 'Dépenses' },
]

export default function Operations({ active = true }) {
  const [operations, setOperations] = useState([])
  const [categories, setCategories] = useState([])
  const [parametres, setParametres] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false) // Rafraîchissement en arrière-plan (ne cache pas le contenu)
  const [searchTerm, setSearchTerm] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [typeFilter, setTypeFilter] = useState('tout') // 'tout', 'entrée', 'dépense'
  const [filter, setFilter] = useState({ mois: null, annee: null })
  const [showPeriodFilters, setShowPeriodFilters] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingOperation, setEditingOperation] = useState(null)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [selectedOperation, setSelectedOperation] = useState(null)
  const [comptes, setComptes] = useState([])
  // Identifiant de l'opération en cours de suppression : évite le double clic,
  // qui produisait une seconde requête et une erreur « opération non trouvée ».
  const [deletingId, setDeletingId] = useState(null)
  const toast = useToast()
  const confirm = useConfirm()
  const hasLoadedRef = useRef(false)

  const loadData = useCallback(async ({ forceRefresh = false, signal } = {}) => {
    const useBackground = hasLoadedRef.current
    try {
      if (useBackground) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      const [opsResponse, catsData, paramsData, comptesData] = await Promise.all([
        getOperations(filter, forceRefresh, { signal }).catch(err => {
          if (isAbortError(err)) throw err
          console.error('Erreur lors du chargement des opérations:', err)
          return []
        }),
        getCategories(forceRefresh, { signal }).catch(err => {
          if (isAbortError(err)) throw err
          console.error('Erreur lors du chargement des catégories:', err)
          return []
        }),
        getParametres(forceRefresh, { signal }).catch(err => {
          if (isAbortError(err)) throw err
          console.error('Erreur lors du chargement des paramètres:', err)
          return { devise: 'EUR', decouvert_autorise: 0 }
        }),
        getComptes(forceRefresh, { signal }).catch(err => {
          if (isAbortError(err)) throw err
          return []
        }),
      ])
      if (signal?.aborted) return

      const operationsArray = Array.isArray(opsResponse)
        ? opsResponse
        : (opsResponse?.data || [])

      setOperations(operationsArray)
      setCategories(catsData || [])
      setParametres(paramsData)
      setComptes(Array.isArray(comptesData) ? comptesData : [])
      hasLoadedRef.current = true
    } catch (error) {
      if (isAbortError(error) || signal?.aborted) return
      console.error('Erreur lors du chargement des données:', error)
      if (!hasLoadedRef.current) {
        setOperations([])
        setCategories([])
        setParametres(null)
      }
    } finally {
      if (signal?.aborted) return
      setRefreshing(false)
      setLoading(false)
    }
  }, [filter])

  useFreshData({ onRefresh: loadData, enabled: active })

  const compteNomById = (compteId) =>
    compteId != null ? (comptes.find((c) => c.id === compteId)?.nom || '') : ''

  // Filtrer les opérations
  const filteredOperations = operations.filter(op => {
    // Filtre par type
    if (typeFilter === 'entrée' && op.type !== 'entrée') return false
    if (typeFilter === 'dépense' && op.type === 'entrée') return false

    // Filtre par recherche : le montant et le compte sont les deux critères par
    // lesquels on cherche le plus souvent une opération précise.
    if (searchTerm) {
      const search = searchTerm.toLowerCase().replace(',', '.')
      return (
        op.description?.toLowerCase().includes(search) ||
        op.categorie?.toLowerCase().includes(search) ||
        op.type?.toLowerCase().includes(search) ||
        String(op.montant ?? '').includes(search) ||
        compteNomById(op.compte_id).toLowerCase().includes(search)
      )
    }

    return true
  })

  // Totaux de la sélection courante : sans eux, filtrer ne répond pas à la
  // question qu'on se pose en filtrant (« combien ai-je dépensé ici ? »).
  const totauxFiltres = filteredOperations.reduce(
    (acc, op) => {
      const montant = parseFloat(op.montant) || 0
      if (op.type === 'entrée') acc.entrees += montant
      else acc.depenses += montant
      return acc
    },
    { entrees: 0, depenses: 0 }
  )

  const hasActiveFilters = Boolean(searchTerm || typeFilter !== 'tout' || filter.mois || filter.annee)

  const resetFilters = () => {
    setSearchTerm('')
    setShowSearch(false)
    setTypeFilter('tout')
    setFilter({ mois: null, annee: null })
  }

  // Grouper les opérations par date (locale, pas UTC : sinon « aujourd’hui »
  // bascule de jour après 22 h en France).
  const groupedOperations = filteredOperations.reduce((groups, op) => {
    const { key: dateKey, label: dateLabel, date } = formatOperationDayLabel(op.date)

    if (!groups[dateKey]) {
      groups[dateKey] = { label: dateLabel, date, operations: [] }
    }
    groups[dateKey].operations.push(op)
    return groups
  }, {})

  // Trier les groupes par date (plus récent en premier)
  const sortedGroups = Object.entries(groupedOperations).sort((a, b) => {
    return new Date(b[1].date) - new Date(a[1].date)
  })

  const handleEdit = (op) => {
    setEditingOperation(op)
    setModalOpen(true)
  }

  /** Recrée une opération supprimée par erreur (bouton « Annuler » du toast). */
  const restoreOperation = async (op) => {
    try {
      await addOperation({
        type: op.type,
        montant: parseFloat(op.montant),
        description: op.description || '',
        categorie: op.categorie,
        date: formatLocalISO(op.date),
        compte_id: op.compte_id ?? null,
      })
      window.dispatchEvent(new Event('operationChanged'))
      toast.success('Opération restaurée')
    } catch (error) {
      console.error('Erreur lors de la restauration:', error)
      toast.error(error.message || 'Impossible de restaurer l’opération')
    }
  }

  const handleDelete = async (op) => {
    if (deletingId) return
    const devise = parametres?.devise || 'EUR'
    const compteNom = compteNomById(op.compte_id)

    // La confirmation rappelle sur quoi elle porte : montant, date et compte
    // sont ce qui permet de vérifier qu'on supprime la bonne ligne.
    const confirmed = await confirm({
      title: 'Supprimer cette opération ?',
      description: 'Les soldes seront recalculés en conséquence.',
      danger: true,
      confirmLabel: 'Supprimer',
      details: [
        { label: 'Catégorie', value: op.categorie || 'Opération' },
        {
          label: 'Montant',
          value: `${op.type === 'entrée' ? '+' : '-'} ${formatMoneySimple(parseFloat(op.montant), devise)}`,
          className: op.type === 'entrée' ? 'text-positive' : 'text-negative',
        },
        {
          label: 'Date',
          value: formatOperationLongDate(op.date),
        },
        ...(compteNom ? [{ label: 'Compte', value: compteNom }] : []),
        ...(op.description ? [{ label: 'Description', value: op.description }] : []),
      ],
    })
    if (!confirmed) return

    setDeletingId(op.id)
    try {
      await deleteOperation(op.id)
      window.dispatchEvent(new Event('operationChanged'))
      toast.success('Opération supprimée', {
        action: { label: 'Annuler', onClick: () => restoreOperation(op) },
      })
    } catch (error) {
      console.error('Erreur lors de la suppression:', error)
      toast.error(error.message || 'Erreur lors de la suppression')
    } finally {
      setDeletingId(null)
    }
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setEditingOperation(null)
  }

  const handleDetailModalOpen = (op) => {
    setSelectedOperation(op)
    setDetailModalOpen(true)
  }

  const handleDetailModalClose = () => {
    setDetailModalOpen(false)
    setSelectedOperation(null)
  }

  // Échap ferme le détail, comme n'importe quelle fenêtre modale.
  useEffect(() => {
    if (!detailModalOpen) return undefined
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleDetailModalClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [detailModalOpen])

  const handleModalSave = async () => {
    window.dispatchEvent(new Event('operationChanged'))
    setEditingOperation(null)
  }

  const setQuickFilter = (type) => {
    const now = new Date()
    if (type === 'mois') {
      setFilter({ mois: String(now.getMonth() + 1).padStart(2, '0'), annee: String(now.getFullYear()) })
    } else if (type === 'annee') {
      setFilter({ mois: null, annee: String(now.getFullYear()) })
    } else {
      setFilter({ mois: null, annee: null })
    }
  }

  const handleMonthChange = (e) => {
    const mois = e.target.value
    setFilter({ ...filter, mois: mois ? mois.padStart(2, '0') : null })
  }

  const handleYearChange = (e) => {
    setFilter({ ...filter, annee: e.target.value || null })
  }

  // Générer les années disponibles (5 dernières années + année actuelle + 1 future)
  const currentYear = new Date().getFullYear()
  const years = []
  for (let i = currentYear - 5; i <= currentYear + 1; i++) {
    years.push(i)
  }

  const activePeriode = filter.mois ? 'mois' : filter.annee ? 'annee' : 'all'
  const devise = parametres?.devise || 'EUR'
  const solde = totauxFiltres.entrees - totauxFiltres.depenses

  const openNewOperation = () => {
    setEditingOperation(null)
    setModalOpen(true)
  }

  return (
    // L'écran avait sa propre barre collante en plus du Header global : deux
    // bandeaux se superposaient en haut de page. Il utilise désormais la même
    // structure que les autres écrans.
    <div className="app-page pb-24">
      <div className="app-page-header">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="app-page-title">Opérations</h1>
            {(loading || refreshing) && (
              <span className="flex items-center gap-1.5 text-sm text-ink-muted" role="status">
                <Icon name="refresh" size={14} className="animate-spin" />
                {hasLoadedRef.current ? 'Actualisation…' : 'Chargement…'}
              </span>
            )}
          </div>
          <p className="app-page-subtitle">
            Historique de vos entrées et dépenses, groupées par date.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              const next = !showSearch
              setShowSearch(next)
              if (!next) setSearchTerm('')
            }}
            className={`btn-icon ${showSearch || searchTerm ? 'bg-surface-active text-ink' : ''}`}
            aria-label="Rechercher"
            aria-pressed={showSearch}
          >
            <Icon name="search" size={18} />
          </button>
          <button
            type="button"
            onClick={() => setShowPeriodFilters((v) => !v)}
            className={`btn-icon ${showPeriodFilters || filter.mois || filter.annee ? 'bg-surface-active text-ink' : ''}`}
            aria-label="Filtrer par période"
            aria-expanded={showPeriodFilters}
          >
            <Icon name="filter" size={18} />
          </button>
          <button type="button" onClick={openNewOperation} className="btn-primary">
            <Icon name="plus" size={16} />
            <span className="hidden sm:inline">Ajouter</span>
          </button>
        </div>
      </div>

      {/* Barre de filtres */}
      <section className="card p-3" aria-label="Filtres">
        <div className="flex flex-wrap items-center gap-2">
          <div className="segmented" role="group" aria-label="Type d’opération">
            {TYPE_FILTERS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTypeFilter(t.key)}
                aria-pressed={typeFilter === t.key}
                className={`segmented-item ${typeFilter === t.key ? 'segmented-item-active' : ''}`}
              >
                {t.key === 'entrée' && <Icon name="arrowDown" size={13} className="text-positive" />}
                {t.key === 'dépense' && <Icon name="arrowUp" size={13} className="text-negative" />}
                {t.label}
              </button>
            ))}
          </div>

          <div className="segmented" role="group" aria-label="Période">
            <button
              type="button"
              onClick={() => setQuickFilter('mois')}
              aria-pressed={activePeriode === 'mois'}
              className={`segmented-item ${activePeriode === 'mois' ? 'segmented-item-active' : ''}`}
            >
              Ce mois
            </button>
            <button
              type="button"
              onClick={() => setQuickFilter('annee')}
              aria-pressed={activePeriode === 'annee'}
              className={`segmented-item ${activePeriode === 'annee' ? 'segmented-item-active' : ''}`}
            >
              Cette année
            </button>
            <button
              type="button"
              onClick={() => setQuickFilter('all')}
              aria-pressed={activePeriode === 'all'}
              className={`segmented-item ${activePeriode === 'all' ? 'segmented-item-active' : ''}`}
            >
              Tout
            </button>
          </div>

          {hasActiveFilters && (
            <button type="button" onClick={resetFilters} className="btn-ghost btn-sm ml-auto">
              <Icon name="close" size={14} />
              Réinitialiser
            </button>
          )}
        </div>

        {showSearch && (
          <div className="relative mt-3">
            <Icon
              name="search"
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"
            />
            <input
              type="search"
              placeholder="Catégorie, description, montant, compte…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSearchTerm('')
                  setShowSearch(false)
                }
              }}
              className="input pl-9 pr-9"
              autoFocus
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-ink-muted transition-colors duration-fast hover:bg-surface-hover hover:text-ink"
                aria-label="Effacer la recherche"
              >
                <Icon name="close" size={14} />
              </button>
            )}
          </div>
        )}

        {showPeriodFilters && (
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <div className="field">
              <label htmlFor="filtre-mois" className="field-label">Mois</label>
              <select id="filtre-mois" value={filter.mois || ''} onChange={handleMonthChange} className="select">
                <option value="">Tous les mois</option>
                {MONTHS.map((month, index) => (
                  <option key={month} value={String(index + 1).padStart(2, '0')}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="filtre-annee" className="field-label">Année</label>
              <select id="filtre-annee" value={filter.annee || ''} onChange={handleYearChange} className="select">
                <option value="">Toutes les années</option>
                {years.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </section>

      {/* Récapitulatif de la sélection courante */}
      {filteredOperations.length > 0 && (
        <section
          className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 rounded-lg border border-line bg-surface-sunken px-4 py-3"
          aria-label="Total de la sélection"
        >
          <span className="text-base text-ink-secondary">
            {filteredOperations.length} opération{filteredOperations.length > 1 ? 's' : ''}
            {hasActiveFilters && operations.length !== filteredOperations.length && (
              <span className="text-ink-muted"> sur {operations.length}</span>
            )}
          </span>
          <div className="flex items-center gap-5 text-base">
            <span className="flex items-center gap-1.5">
              <Icon name="arrowDown" size={14} className="text-positive" />
              <Amount value={totauxFiltres.entrees} devise={devise} tone="positive" className="font-medium" />
            </span>
            <span className="flex items-center gap-1.5">
              <Icon name="arrowUp" size={14} className="text-negative" />
              <Amount value={totauxFiltres.depenses} devise={devise} tone="negative" className="font-medium" />
            </span>
            <span className="flex items-center gap-1.5 border-l border-line pl-5">
              <span className="text-ink-muted">Solde</span>
              <Amount
                value={solde}
                devise={devise}
                signed
                tone={solde < 0 ? 'negative' : 'none'}
                className="font-semibold"
              />
            </span>
          </div>
        </section>
      )}

      {/* Liste des opérations groupées par date */}
      {loading && !hasLoadedRef.current ? (
        <div className="card divide-y divide-line">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-3.5 px-4 py-3.5">
              <div className="skeleton h-9 w-9 rounded-md" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-3.5 w-40" />
                <div className="skeleton h-3 w-24" />
              </div>
              <div className="skeleton h-4 w-20" />
            </div>
          ))}
        </div>
      ) : sortedGroups.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <span className="empty-state-icon">
              <Icon name={operations.length === 0 ? 'operations' : 'search'} size={20} />
            </span>
            <p className="empty-state-title">
              {operations.length === 0
                ? 'Aucune opération pour le moment'
                : 'Aucun résultat'}
            </p>
            <p className="empty-state-text">
              {operations.length === 0
                ? 'Ajoutez votre première opération pour voir vos soldes et vos prévisions se construire.'
                : 'Aucune opération ne correspond à ces filtres. Essayez d’en retirer un.'}
            </p>
            {operations.length === 0 ? (
              <button type="button" onClick={openNewOperation} className="btn-primary mt-1">
                <Icon name="plus" size={16} />
                Ajouter une opération
              </button>
            ) : (
              // Sortir d'une liste vide sans avoir à défaire les filtres un par un.
              <button type="button" onClick={resetFilters} className="btn-secondary mt-1">
                Réinitialiser les filtres
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {sortedGroups.map(([dateKey, group]) => {
            const dateObj = new Date(group.date)
            const totalJour = group.operations.reduce(
              (sum, op) => sum + (op.type === 'entrée' ? 1 : -1) * (parseFloat(op.montant) || 0),
              0
            )

            return (
              <section key={dateKey} aria-label={group.label}>
                {/* En-tête de date : collant, pour qu'on sache toujours à quel
                    jour appartient la ligne qu'on est en train de lire. */}
                <div className="sticky top-14 z-10 -mx-1 flex items-baseline justify-between gap-4 bg-canvas px-1 py-2">
                  <h2 className="text-base font-semibold capitalize text-ink">{group.label}</h2>
                  <div className="flex items-baseline gap-3 text-sm">
                    <span className="text-ink-muted">{dateObj.getFullYear()}</span>
                    <Amount
                      value={totalJour}
                      devise={devise}
                      signed
                      tone={totalJour < 0 ? 'negative' : 'positive'}
                      className="font-medium"
                    />
                  </div>
                </div>

                <div className="card divide-y divide-line">
                  {group.operations.map((op) => {
                    const isEntree = op.type === 'entrée'
                    const montant = parseFloat(op.montant) || 0
                    const soldeApres = parseFloat(op.solde_apres_operation || 0)
                    const compteNom = compteNomById(op.compte_id)
                    const categorie = op.categorie || 'Opération'
                    const hasDescription =
                      op.description && op.description.trim() !== '' && op.description !== op.categorie

                    return (
                      <div
                        key={op.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleDetailModalOpen(op)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleDetailModalOpen(op)
                          }
                        }}
                        // `group` permet de ne révéler les actions qu'au survol
                        // de la ligne : la liste reste calme au repos.
                        className="group flex cursor-pointer items-center gap-3.5 px-4 py-3 transition-colors duration-fast hover:bg-surface-hover"
                      >
                        <span
                          aria-hidden="true"
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md border ${
                            isEntree
                              ? 'border-positive-border bg-positive-subtle text-positive'
                              : 'border-line bg-surface-sunken text-ink-secondary'
                          }`}
                        >
                          <Icon name={categoryIcon(op.categorie, op.type)} size={17} />
                        </span>

                        <div className="min-w-0 flex-1">
                          <p className="truncate text-base font-medium text-ink" title={categorie}>
                            {categorie}
                          </p>
                          <p className="truncate text-sm text-ink-muted">
                            {[compteNom, hasDescription ? op.description : null]
                              .filter(Boolean)
                              .join(' · ') || (isEntree ? 'Entrée' : 'Dépense')}
                          </p>
                        </div>

                        {/* Largeur fixe : les montants s'alignent d'une ligne à
                            l'autre quelle que soit la longueur du libellé. */}
                        <div className="w-[7.5rem] shrink-0 text-right sm:w-36">
                          <Amount
                            value={isEntree ? montant : -montant}
                            devise={devise}
                            signed
                            tone={isEntree ? 'positive' : 'none'}
                            className="block text-base font-medium"
                          />
                          <span className="mt-0.5 block text-xs text-ink-muted">
                            Solde <Amount value={soldeApres} devise={devise} tone={soldeApres < 0 ? 'negative' : 'none'} />
                          </span>
                        </div>

                        <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity duration-fast focus-within:opacity-100 group-hover:opacity-100 max-sm:opacity-100">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleEdit(op)
                            }}
                            className="btn-icon h-8 w-8"
                            aria-label={`Modifier l’opération ${categorie}`}
                            title="Modifier"
                          >
                            <Icon name="edit" size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDelete(op)
                            }}
                            disabled={deletingId === op.id}
                            className="btn-icon h-8 w-8 hover:bg-negative-subtle hover:text-negative"
                            aria-label={`Supprimer l’opération ${categorie}`}
                            title="Supprimer"
                          >
                            <Icon
                              name={deletingId === op.id ? 'refresh' : 'trash'}
                              size={16}
                              className={deletingId === op.id ? 'animate-spin' : ''}
                            />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </section>
            )
          })}
        </div>
      )}

      {/* Ajout rapide au pouce : réservé au mobile, où le bouton de l'en-tête
          peut être hors d'atteinte après un défilement. */}
      <button
        type="button"
        onClick={openNewOperation}
        className="fixed bottom-24 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-contrast shadow-lg transition-colors duration-fast hover:bg-accent-hover sm:hidden"
        aria-label="Ajouter une opération"
      >
        <Icon name="plus" size={24} />
      </button>

      {/* Modal d'édition/ajout */}
      {modalOpen && (
        <OperationModal
          operation={editingOperation}
          categories={categories}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}

      {/* Modal de détail de l'opération */}
      {detailModalOpen && selectedOperation && (
        <div className="modal-backdrop z-[60]" onClick={handleDetailModalClose} role="presentation">
          <div
            className="modal-panel modal-panel-lg"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="operation-detail-title"
          >
            <div className="modal-header">
              <div className="flex min-w-0 items-center gap-3">
                <span
                  aria-hidden="true"
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md border ${
                    selectedOperation.type === 'entrée'
                      ? 'border-positive-border bg-positive-subtle text-positive'
                      : 'border-line bg-surface-sunken text-ink-secondary'
                  }`}
                >
                  <Icon name={categoryIcon(selectedOperation.categorie, selectedOperation.type)} size={17} />
                </span>
                <h2 id="operation-detail-title" className="modal-title truncate">
                  {selectedOperation.categorie || 'Opération'}
                </h2>
              </div>
              <button
                type="button"
                onClick={handleDetailModalClose}
                className="btn-icon -mr-1.5"
                aria-label="Fermer"
              >
                <Icon name="close" size={18} />
              </button>
            </div>

            <div className="modal-body">
              {/* Le montant est l'information principale : il est sorti de la
                  liste de champs pour être lisible d'un coup d'œil. */}
              <div className="mb-4 rounded-lg border border-line bg-surface-sunken px-4 py-4 text-center">
                <p className="overline">{selectedOperation.type === 'entrée' ? 'Entrée' : 'Dépense'}</p>
                <Amount
                  value={
                    selectedOperation.type === 'entrée'
                      ? parseFloat(selectedOperation.montant)
                      : -parseFloat(selectedOperation.montant)
                  }
                  devise={devise}
                  signed
                  tone={selectedOperation.type === 'entrée' ? 'positive' : 'none'}
                  className="mt-1.5 block text-3xl font-semibold"
                />
              </div>

              <div className="data-row">
                <span className="data-label">Date</span>
                <span className="data-value font-normal">
                  {formatOperationLongDate(selectedOperation.date, { weekday: true })}
                </span>
              </div>
              {compteNomById(selectedOperation.compte_id) && (
                <div className="data-row">
                  <span className="data-label">Compte</span>
                  <span className="data-value font-normal">
                    {compteNomById(selectedOperation.compte_id)}
                  </span>
                </div>
              )}
              {selectedOperation.solde_apres_operation !== undefined && (
                <div className="data-row">
                  <span className="data-label">Solde après opération</span>
                  <span className="data-value">
                    <Amount
                      value={parseFloat(selectedOperation.solde_apres_operation)}
                      devise={devise}
                      tone={parseFloat(selectedOperation.solde_apres_operation) < 0 ? 'negative' : 'none'}
                    />
                  </span>
                </div>
              )}

              {selectedOperation.description && selectedOperation.description.trim() !== '' && (
                <div className="mt-4 border-t border-line pt-4">
                  <h3 className="overline mb-2">Description</h3>
                  <p className="whitespace-pre-wrap break-words text-base text-ink-secondary">
                    {selectedOperation.description}
                  </p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                onClick={() => {
                  handleDetailModalClose()
                  handleDelete(selectedOperation)
                }}
                className="btn-danger-ghost"
              >
                <Icon name="trash" size={15} />
                Supprimer
              </button>
              <button
                type="button"
                onClick={() => {
                  handleDetailModalClose()
                  handleEdit(selectedOperation)
                }}
                className="btn-primary ml-auto"
              >
                <Icon name="edit" size={15} />
                Modifier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
